import { useCallback, useState } from "react";
import { useSalon } from "../../../context/SalonContext";
import { useApp } from "../../../context/AppContext";
import usePermissions from "../../../hooks/usePermissions";
import { useSalonScopedFetch } from "../../../hooks/useSalonScopedFetch";
import { Employee } from "../../../types/salon";
import { CommissionRecord } from "../../../types/commission";
import { filterActiveStaff } from "../../../utils/staffUtils";
import { groupCommissionsByStaff } from "../../../utils/commissionUtils";

export interface StaffCommissionsState {
  isLoading: boolean;
  loadError: string | null;
  setLoadError: (error: string | null) => void;
  /** Each staff member's commission records, keyed by staff id. */
  commissionsByStaff: Record<string, CommissionRecord[]>;
  /** Active staff the caller may see ("own" scope → only themselves). */
  employees: Employee[];
  canEdit: boolean;
  scope: string;
  refresh: () => Promise<void>;
  retry: () => void;
  /** Deletes a record, then refreshes. Rejects with the API error. */
  deleteRecord: (commissionId: string) => Promise<void>;
}

/**
 * Data + permissions for the Commission Rates views: loads every commission
 * record once per salon (useSalonScopedFetch, so identity churn can't refire
 * it), groups them by staff member, and applies the settingsCommissions scope
 * and edit permission. Same rules as the legacy CommissionRatesTab.
 */
export function useStaffCommissions(): StaffCommissionsState {
  const { businessInfo, employees, currentUser } = useSalon() as {
    businessInfo: any;
    employees: Employee[] | null;
    currentUser: any;
  };
  const { getStaffCommissions, deleteCommission } = useApp();
  const perms = usePermissions()?.settingsCommissions;
  const canEdit = perms?.canEdit !== false;
  const scope = perms?.scope || "own";
  const currentUserId = currentUser?.id || "";

  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [commissionsByStaff, setCommissionsByStaff] = useState<
    Record<string, CommissionRecord[]>
  >({});

  const refresh = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const response = await getStaffCommissions();
      setCommissionsByStaff(groupCommissionsByStaff(response.commissions));
    } catch (error: any) {
      console.error("Error fetching commissions:", error);
      setLoadError(
        error?.message || "Failed to load commissions. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  }, [getStaffCommissions]);

  const { retry } = useSalonScopedFetch(businessInfo?.id, refresh, {
    enabled: Boolean(businessInfo && employees && currentUser),
    tag: "commissions",
  });

  const deleteRecord = useCallback(
    async (commissionId: string) => {
      await deleteCommission(commissionId);
      await refresh();
    },
    [deleteCommission, refresh],
  );

  const active = employees ? filterActiveStaff(employees) : [];
  const visible =
    scope === "own" && currentUserId
      ? active.filter((emp) => emp.id === currentUserId)
      : active;

  return {
    isLoading,
    loadError,
    setLoadError,
    commissionsByStaff,
    employees: visible,
    canEdit,
    scope,
    refresh,
    retry,
    deleteRecord,
  };
}
